// Notes service — CRUD for highlights + bookmarks, plus a markdown
// exporter that lays out a per-book annotation document.
//
// Cloud-sync model (2026-06-26): every write is mirrored to /lib/highlight
// which both persists the JSON copy and inserts into brain.db so other
// apps (dashboards, Tidbyt widgets, analysis pipelines) can read recent
// highlights as ordinary brain items. Reads merge cloud → local on book
// open. Network failures never block — local Dexie stays authoritative
// from the user's perspective.

import { db } from "./db";
import type {
  Bookmark,
  Highlight,
  HighlightColor,
} from "@/types/highlight";

const LIB_BASE = "/lib";

function uid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return (
    "h-" + Math.random().toString(36).slice(2) + Date.now().toString(36)
  );
}

/** Edition-tolerant content fingerprint of a passage — normalised (case +
 *  whitespace folded) djb2 plus length. Lets an annotation relocate across
 *  compatible copies of a book when its CFI won't resolve. */
function contentHash(s: string): string {
  const n = (s || "").replace(/\s+/g, " ").trim().toLowerCase();
  let h = 5381;
  for (let i = 0; i < n.length; i++) h = ((h << 5) + h + n.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36) + "-" + n.length;
}

interface CloudHighlight extends Highlight {
  bookSha?: string;
  bookTitle?: string;
  bookAuthor?: string;
}

async function pushHighlightToCloud(h: Highlight): Promise<void> {
  // Look up book metadata to attach. The server uses bookSha to group
  // highlights by book, and bookTitle/Author to surface them in
  // dashboards without joining back to PageVoice's book table.
  let bookSha: string | undefined;
  let bookTitle: string | undefined;
  let bookAuthor: string | undefined;
  try {
    const book = await db.books.get(h.bookId);
    bookSha = book?.cloudSha256;
    bookTitle = book?.title;
    bookAuthor = book?.author;
  } catch {
    /* book lookup is best-effort */
  }
  const payload: CloudHighlight = {
    ...h,
    bookSha,
    bookTitle,
    bookAuthor,
  };
  try {
    await fetch(`${LIB_BASE}/highlight`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.warn("[pagevoice] cloud push for highlight failed", err);
  }
}

async function deleteHighlightFromCloud(id: string): Promise<void> {
  try {
    await fetch(`${LIB_BASE}/highlight/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  } catch (err) {
    console.warn("[pagevoice] cloud delete for highlight failed", err);
  }
}

/** Fetch every cloud highlight for the given book SHA. Used at book
 *  open to pull in highlights that originated on another device, plus
 *  to recover after a PWA reinstall wiped the local Dexie. */
export async function fetchCloudHighlightsForBook(
  bookSha: string,
): Promise<CloudHighlight[]> {
  try {
    const r = await fetch(
      `${LIB_BASE}/highlights/by-book/${encodeURIComponent(bookSha)}`,
    );
    if (!r.ok) return [];
    const arr = (await r.json()) as CloudHighlight[];
    return Array.isArray(arr) ? arr : [];
  } catch (err) {
    console.warn("[pagevoice] cloud highlights fetch failed", err);
    return [];
  }
}

/** Two-way sync of highlights for a book between local Dexie and the
 *  cloud /lib store. Cloud → local: copy down any highlights newer in
 *  the cloud (added on another device, or recovered after a PWA
 *  reinstall). Local → cloud: backfill anything that exists locally but
 *  not in the cloud — needed once on this device after the feature
 *  shipped so the user's pre-cloud-sync highlights make their way into
 *  brain.db. Called on book open from ReaderView; both directions are
 *  best-effort and non-blocking. */
export async function syncHighlightsForBook(
  bookId: string,
  bookSha: string,
): Promise<void> {
  const cloud = await fetchCloudHighlightsForBook(bookSha);
  const cloudById = new Map(cloud.map((c) => [c.id, c]));

  // Cloud → local merge.
  for (const c of cloud) {
    try {
      const local = await db.highlights.get(c.id);
      // Strip cloud-only fields; carry everything else (tags, kind, rating,
      // visibility, textHash, followUp…) so the annotation model round-trips.
      const { bookSha: _s, bookTitle: _t, bookAuthor: _a, ...clean } = c;
      void _s; void _t; void _a;
      if (!local) {
        await db.highlights.put({ ...clean, bookId });
      } else if ((c.updatedAt ?? 0) > (local.updatedAt ?? 0)) {
        await db.highlights.put({ ...local, ...clean, bookId });
      }
    } catch (err) {
      console.warn("[pagevoice] cloud→local merge failed for", c.id, err);
    }
  }

  // Local → cloud backfill.
  try {
    const locals = await db.highlights
      .where("bookId")
      .equals(bookId)
      .toArray();
    for (const l of locals) {
      const c = cloudById.get(l.id);
      if (!c || (l.updatedAt ?? 0) > (c.updatedAt ?? 0)) {
        await pushHighlightToCloud(l);
      }
    }
  } catch (err) {
    console.warn("[pagevoice] local→cloud backfill failed", err);
  }
}

// ─── Highlights ───────────────────────────────────────────────────────

export async function addHighlight(input: {
  bookId: string;
  cfiRange: string;
  text: string;
  context?: string;
  href?: string;
  chapterLabel?: string;
  color: HighlightColor;
  note?: string;
  tags?: string[];
  followUp?: string;
}): Promise<Highlight> {
  const now = Date.now();
  const h: Highlight = {
    id: uid(),
    bookId: input.bookId,
    cfiRange: input.cfiRange,
    text: input.text,
    context: input.context,
    href: input.href,
    chapterLabel: input.chapterLabel,
    color: input.color,
    note: input.note,
    createdAt: now,
    updatedAt: now,
    // Portable annotation model — private by default, fingerprinted so it can
    // relocate across editions and become a signed event later.
    kind: "highlight",
    visibility: "private",
    tags: input.tags && input.tags.length ? input.tags : undefined,
    followUp: input.followUp,
    textHash: input.text ? contentHash(input.text) : undefined,
  };
  await db.highlights.add(h);
  // Fire-and-forget cloud mirror — Dexie is the source of truth for the
  // UI, the cloud copy is for cross-device sync and brain.db pipelines.
  void pushHighlightToCloud(h);
  return h;
}

export async function updateHighlight(
  id: string,
  patch: Partial<Pick<Highlight,
    "color" | "note" | "tags" | "rating" | "visibility" | "followUp" | "aiResponse">>
): Promise<void> {
  const cur = await db.highlights.get(id);
  if (!cur) return;
  const next = { ...cur, ...patch, updatedAt: Date.now() };
  await db.highlights.put(next);
  void pushHighlightToCloud(next);
}

export async function deleteHighlight(id: string): Promise<void> {
  await db.highlights.delete(id);
  void deleteHighlightFromCloud(id);
}

/** Passage highlights for the notebook — excludes book-level reviews, which
 *  live in the same table under kind "review". */
export async function listHighlights(bookId: string): Promise<Highlight[]> {
  const rows = await db.highlights
    .where("bookId")
    .equals(bookId)
    .sortBy("createdAt");
  return rows.filter((r) => r.kind !== "review");
}

// ─── Book reviews (same pipeline, kind="review", book-level) ───────────

/** The user's review of a book, if any. One per book. */
export async function getBookReview(bookId: string): Promise<Highlight | undefined> {
  const rows = await db.highlights.where("bookId").equals(bookId).toArray();
  return rows.find((r) => r.kind === "review");
}

/** Create or update the book review (star rating + prose). A review is just
 *  a book-level annotation, so it rides the exact same storage, cloud-sync,
 *  and (later) publishing pipeline as a highlight. */
export async function setBookReview(
  bookId: string,
  rating: number | undefined,
  body: string | undefined,
): Promise<Highlight> {
  const now = Date.now();
  const existing = await getBookReview(bookId);
  if (existing) {
    const next: Highlight = { ...existing, rating, note: body, updatedAt: now };
    await db.highlights.put(next);
    void pushHighlightToCloud(next);
    return next;
  }
  const h: Highlight = {
    id: uid(),
    bookId,
    cfiRange: "",
    text: "",
    color: "yellow",
    note: body,
    rating,
    kind: "review",
    visibility: "private",
    createdAt: now,
    updatedAt: now,
  };
  await db.highlights.add(h);
  void pushHighlightToCloud(h);
  return h;
}

// ─── Bookmarks ────────────────────────────────────────────────────────

export async function addBookmark(input: {
  bookId: string;
  cfi: string;
  href?: string;
  chapterLabel?: string;
  label?: string;
}): Promise<Bookmark> {
  const now = Date.now();
  const b: Bookmark = {
    id: uid(),
    bookId: input.bookId,
    cfi: input.cfi,
    href: input.href,
    chapterLabel: input.chapterLabel,
    label: input.label,
    createdAt: now,
  };
  await db.bookmarks.add(b);
  return b;
}

export async function deleteBookmark(id: string): Promise<void> {
  await db.bookmarks.delete(id);
}

export async function listBookmarks(bookId: string): Promise<Bookmark[]> {
  return db.bookmarks
    .where("bookId")
    .equals(bookId)
    .sortBy("createdAt");
}

// ─── Markdown export ──────────────────────────────────────────────────

export async function exportMarkdown(
  bookId: string,
  bookTitle: string,
  bookAuthor?: string
): Promise<string> {
  const [highlights, bookmarks, review] = await Promise.all([
    listHighlights(bookId),
    listBookmarks(bookId),
    getBookReview(bookId),
  ]);

  const parts: string[] = [];
  parts.push(`# ${bookTitle}`);
  if (bookAuthor) parts.push(`*${bookAuthor}*`);
  parts.push("");
  parts.push(`Exported from PageVoice on ${new Date().toISOString().slice(0, 10)}`);
  parts.push("");

  // Book review + rating at the top, if the reader wrote one.
  if (review && (review.rating || review.note)) {
    parts.push("## Your review");
    parts.push("");
    if (review.rating) {
      parts.push("★".repeat(review.rating) + "☆".repeat(Math.max(0, 5 - review.rating)) +
        `  (${review.rating}/5)`);
      parts.push("");
    }
    if (review.note) {
      parts.push(review.note);
      parts.push("");
    }
  }

  if (highlights.length === 0 && bookmarks.length === 0 && !review) {
    parts.push("_No highlights or bookmarks yet._");
    return parts.join("\n") + "\n";
  }

  // Group highlights by chapter.
  if (highlights.length > 0) {
    parts.push(`## Highlights (${highlights.length})`);
    parts.push("");
    const byChapter = new Map<string, Highlight[]>();
    for (const h of highlights) {
      const key = h.chapterLabel || h.href || "Untitled";
      const arr = byChapter.get(key) ?? [];
      arr.push(h);
      byChapter.set(key, arr);
    }
    for (const [chapter, items] of byChapter) {
      parts.push(`### ${chapter}`);
      parts.push("");
      for (const h of items) {
        parts.push(`> ${h.text.replace(/\n+/g, " ")}`);
        if (h.note) {
          parts.push("");
          parts.push(`**Note:** ${h.note}`);
        }
        if (h.followUp) {
          parts.push("");
          parts.push(`**Follow-up:** ${h.followUp}`);
        }
        if (h.tags && h.tags.length) {
          parts.push("");
          parts.push(h.tags.map((t) => `\`${t}\``).join(" "));
        }
        if (h.aiResponse) {
          parts.push("");
          parts.push(`**AI companion:** ${h.aiResponse}`);
        }
        parts.push("");
        parts.push(`<sub>${h.color} · ${new Date(h.createdAt).toISOString().slice(0, 10)}</sub>`);
        parts.push("");
        parts.push("---");
        parts.push("");
      }
    }
  }

  if (bookmarks.length > 0) {
    parts.push(`## Bookmarks (${bookmarks.length})`);
    parts.push("");
    for (const b of bookmarks) {
      const label = b.label ? ` — ${b.label}` : "";
      const chapter = b.chapterLabel ? ` (${b.chapterLabel})` : "";
      parts.push(`- ${new Date(b.createdAt).toISOString().slice(0, 10)}${chapter}${label}`);
    }
    parts.push("");
  }

  return parts.join("\n") + "\n";
}

/** Trigger a browser download of the markdown export. */
export async function downloadMarkdown(
  bookId: string,
  bookTitle: string,
  bookAuthor?: string
): Promise<void> {
  const md = await exportMarkdown(bookId, bookTitle, bookAuthor);
  const blob = new Blob([md], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeTitle = bookTitle.replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 60);
  a.download = `${safeTitle || "book"}-notes.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 200);
}
